import { NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import {
  RiDashboardFill, RiDashboardLine,
  RiTvFill, RiTvLine,
  RiLayoutGridFill, RiLayoutGridLine,
  RiBarChartBoxFill, RiBarChartBoxLine,
  RiBuilding2Fill, RiBuilding2Line,
  RiLockPasswordFill, RiLockPasswordLine,
} from "react-icons/ri";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/app", label: "Overview", icon: RiDashboardLine, active: RiDashboardFill, end: true, testid: "nav-overview" },
  { to: "/app/tvs", label: "Devices", icon: RiTvLine, active: RiTvFill, testid: "nav-tvs" },
  { to: "/app/video-wall", label: "Video Wall", icon: RiLayoutGridLine, active: RiLayoutGridFill, testid: "nav-video-wall" },
  { to: "/app/analytics", label: "Analytics", icon: RiBarChartBoxLine, active: RiBarChartBoxFill, testid: "nav-analytics" },
  { to: "/app/accounts", label: "Accounts", icon: RiBuilding2Line, active: RiBuilding2Fill, testid: "nav-accounts" },
  { to: "/app/vault", label: "Vault", icon: RiLockPasswordLine, active: RiLockPasswordFill, testid: "nav-vault" },
];

function Logo() {
  return (
    <div className="flex items-center gap-3.5 px-6 pt-8 pb-7" data-testid="sidebar-logo">
      <div className="relative h-12 w-12 shrink-0 rounded-[14px] bg-gradient-to-br from-[#5B7CFA] via-[#5468F0] to-[#7B5CF0] shadow-[0_8px_18px_-6px_rgba(84,104,240,0.6)] flex items-center justify-center">
        <span className="font-heading text-[22px] font-bold leading-none text-white">C</span>
        <span className="absolute bottom-2 right-2 h-1.5 w-1.5 rounded-full bg-[#7FE3F0]" />
      </div>
      <div className="min-w-0">
        <div className="font-heading text-[19px] font-semibold leading-tight text-slate-900">CreatiCore</div>
        <div className="text-[15px] leading-tight text-slate-500 mt-1">Dashboard</div>
      </div>
    </div>
  );
}

function NavItem({ item }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      className={({ isActive }) =>
        `group relative flex items-center gap-4 h-[52px] pl-7 pr-4 mr-3 rounded-r-2xl text-[15px] transition-colors duration-200 ${
          isActive ? "bg-[#EEF0FD] text-[#4C5BD8] font-medium" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      {({ isActive }) => {
        const Icon = isActive ? item.active : item.icon;
        return (
          <>
            {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#4C5BD8]" />}
            <Icon className={`h-[19px] w-[19px] shrink-0 ${isActive ? "text-[#4C5BD8]" : "text-slate-500 group-hover:text-slate-700"}`} />
            <span>{item.label}</span>
          </>
        );
      }}
    </NavLink>
  );
}

function UserCard({ email, accountCode, onLogout }) {
  const initials = (email || "A").split("@")[0].split(/[._-]/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "A";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="mx-3 flex items-center gap-3 rounded-2xl bg-slate-50/80 px-3.5 py-3.5 text-left transition-colors duration-200 hover:bg-slate-100 focus:outline-none"
          data-testid="sidebar-user-card"
        >
          <div className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-[#5B7CFA] to-[#7B5CF0] flex items-center justify-center text-sm font-semibold text-white ring-2 ring-white shadow">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-semibold text-slate-900" data-testid="sidebar-user-email">{email}</div>
            <div className="text-[12px] text-slate-500 whitespace-nowrap" data-testid="sidebar-account-code">
              Administrador{accountCode ? <> · ID <span className="font-mono text-slate-700">{accountCode}</span></> : null}
            </div>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="w-52">
        <DropdownMenuItem onSelect={onLogout} className="text-red-600 focus:text-red-600" data-testid="admin-logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProTip() {
  const navigate = useNavigate();
  return (
    <div className="relative mx-3 mt-4 overflow-hidden rounded-[22px] bg-gradient-to-br from-[#8E93F2] via-[#7A7FEA] to-[#6A6EE0] p-5 text-white shadow-[0_14px_30px_-12px_rgba(106,110,224,0.7)]" data-testid="sidebar-pro-tip">
      <div className="flex items-start justify-between">
        <div className="text-[15px] font-medium">Consejo Pro</div>
        <div className="relative h-6 w-10">
          <span className="absolute left-1 top-3 h-2 w-2 rounded-full bg-[#7FE3C8]" />
          <span className="absolute left-4 top-0.5 h-2 w-2 rounded-full bg-[#9EC5FF]" />
          <span className="absolute left-6 top-4 h-1.5 w-1.5 rounded-full bg-[#7FB8FF]" />
          <span className="absolute left-8 top-1 h-1.5 w-1.5 rounded-full bg-[#F7A8C8]" />
        </div>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-white/90 pr-2">
        Junta tus pantallas en un Video Wall para un impacto gigante.
      </p>
      <div className="mt-5 flex items-end justify-between">
        <button
          type="button"
          onClick={() => navigate("/app/video-wall")}
          className="relative z-10 rounded-xl bg-white px-4 py-2.5 text-[13px] font-medium text-[#4C5BD8] shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
          data-testid="sidebar-pro-tip-cta"
        >
          Crear Video Wall
        </button>
      </div>
      <div className="pointer-events-none absolute -bottom-6 -right-4 h-28 w-28 rounded-full bg-white/10" />
      <svg className="pointer-events-none absolute bottom-6 right-5 h-8 w-16 text-white/80" viewBox="0 0 64 32" fill="none">
        <path d="M2 28 C 12 28, 14 6, 24 8 S 34 30, 42 24 S 54 4, 62 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export default function Sidebar({ email, accountCode, onLogout }) {
  return (
    <aside className="h-screen sticky top-0 w-[272px] shrink-0 p-4 pr-0" data-testid="admin-sidebar">
      <div className="flex h-full flex-col overflow-hidden rounded-[28px] bg-white pb-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_20px_48px_-20px_rgba(76,91,216,0.25)]">
        <Logo />
        <nav className="flex flex-col gap-1.5 overflow-y-auto">
          {NAV.map((item) => <NavItem key={item.to} item={item} />)}
        </nav>
        <div className="flex-1" />
        <UserCard email={email} accountCode={accountCode} onLogout={onLogout} />
        <div className="mx-3 mt-4 h-px bg-slate-100" />
        <ProTip />
      </div>
    </aside>
  );
}
